import React, { useMemo } from 'react';
import { Eye, CheckCircle2, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { Card } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { useQualityWorkspaceState } from './useQualityWorkspaceState';
export function ValidationSuite() {
  const {
    filteredRecords
  } = useQualityWorkspaceState();
  const rows = useMemo(() => filteredRecords.map(record => ({
    ...record,
    issues: record.issues.filter(i => i.category === 'visuals')
  })).filter(record => record.issues.length > 0), [filteredRecords]);
  const summary = useMemo(() => {
    const allIssues = rows.flatMap(r => r.issues);
    return {
      questions: filteredRecords.length,
      affected: rows.length,
      critical: allIssues.filter(i => i.severity === 'critical').length,
      warning: allIssues.filter(i => i.severity === 'warning').length,
      info: allIssues.filter(i => i.severity === 'info').length
    };
  }, [rows, filteredRecords]);
  return <div className="space-y-4" data-id="element-2540">
      <Card className="p-5" data-id="element-2541">
        <div className="flex items-center justify-between mb-4" data-id="element-2542">
          <div className="flex items-center gap-2" data-id="element-2543">
            <div className="p-2 bg-indigo-50 rounded-lg" data-id="element-2544">
              <Eye className="w-5 h-5 text-indigo-600" data-id="element-2545" />
            </div>
            <div data-id="element-2546">
              <h3 className="text-lg font-bold text-slate-900" data-id="element-2547">
                Visual Governance
              </h3>
              <p className="text-sm text-slate-500" data-id="element-2548">
                {summary.affected} of {summary.questions} questions have visual
                issues
              </p>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-2549">
            {summary.critical > 0 && <Badge variant="danger" size="sm" data-id="element-2550">
                {summary.critical} critical
              </Badge>}
            {summary.warning > 0 && <Badge variant="warning" size="sm" data-id="element-2551">
                {summary.warning} warnings
              </Badge>}
            {summary.affected === 0 && <Badge variant="success" size="sm" data-id="element-2552">
                All passing
              </Badge>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-id="element-2553">
          <SummaryBox label="Filtered Questions" value={summary.questions} color="slate" data-id="element-2554" />
          <SummaryBox label="Affected" value={summary.affected} color={summary.affected > 0 ? 'amber' : 'emerald'} data-id="element-2555" />
          <SummaryBox label="Critical" value={summary.critical} color={summary.critical > 0 ? 'rose' : 'emerald'} data-id="element-2556" />
          <SummaryBox label="Warnings" value={summary.warning} color={summary.warning > 0 ? 'amber' : 'emerald'} data-id="element-2557" />
          <SummaryBox label="Info" value={summary.info} color="blue" data-id="element-2558" />
        </div>
      </Card>

      {rows.length === 0 ? <Card className="p-12 text-center" data-id="element-2559">
          <CheckCircle2 className="w-12 h-12 text-emerald-500 mx-auto mb-3" data-id="element-2560" />
          <h3 className="text-lg font-bold text-slate-900" data-id="element-2561">No visual issues</h3>
          <p className="text-slate-500" data-id="element-2562">
            All questions pass visual governance checks in the current filter
            scope.
          </p>
        </Card> : <div className="space-y-3" data-id="element-2563">
          {rows.slice(0, 100).map(record => {
        const hasCritical = record.issues.some(i => i.severity === 'critical');
        return <Card key={record.question.id} className={`p-4 border-l-4 ${hasCritical ? 'border-l-rose-500' : 'border-l-amber-400'}`} data-id="element-2564">
                <div className="flex items-center justify-between mb-3" data-id="element-2565">
                  <code className="text-sm font-bold text-slate-900 bg-slate-100 px-2 py-0.5 rounded" data-id="element-2566">
                    {record.question.id}
                  </code>
                  <div className="flex gap-1.5" data-id="element-2567">
                    <Badge variant="neutral" size="sm" data-id="element-2568">
                      {record.question.module}
                    </Badge>
                    <Badge variant="neutral" size="sm" data-id="element-2569">
                      T{record.question.tier} · G{record.question.group}
                    </Badge>
                  </div>
                </div>
                <ul className="space-y-1.5" data-id="element-2570">
                  {record.issues.map(issue => <li key={issue.id} className="flex items-start gap-2 text-sm" data-id="element-2571">
                      <SeverityIcon severity={issue.severity} data-id="element-2572" />
                      <span className={issue.severity === 'critical' ? 'text-rose-700' : issue.severity === 'warning' ? 'text-amber-700' : 'text-slate-600'} data-id="element-2573">
                        {issue.message}
                      </span>
                    </li>)}
                </ul>
              </Card>;
      })}
          {rows.length > 100 && <p className="text-center text-sm text-slate-500 py-2" data-id="element-2574">
              Showing 100 of {rows.length} — use filters to narrow results.
            </p>}
        </div>}
    </div>;
}
function SummaryBox({
  label,
  value,
  color
}: {
  label: string;
  value: number;
  color: 'slate' | 'rose' | 'amber' | 'emerald' | 'blue';
}) {
  const colors = {
    slate: 'bg-slate-50 border-slate-200 text-slate-700',
    rose: 'bg-rose-50 border-rose-200 text-rose-700',
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    emerald: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    blue: 'bg-blue-50 border-blue-200 text-blue-700'
  };
  return <div className={`rounded-xl border p-3 text-center ${colors[color]}`} data-id="element-2575">
      <div className="text-2xl font-black" data-id="element-2576">{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider opacity-70 mt-0.5" data-id="element-2577">
        {label}
      </div>
    </div>;
}
function SeverityIcon({
  severity
}: {
  severity: string;
}) {
  if (severity === 'critical') return <AlertTriangle className="w-4 h-4 text-rose-500 shrink-0 mt-0.5" data-id="element-2578" />;
  if (severity === 'warning') return <AlertCircle className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" data-id="element-2579" />;
  return <Info className="w-4 h-4 text-blue-400 shrink-0 mt-0.5" data-id="element-2580" />;
}